import { Icon } from "@iconify/react";
import Breadcrumbs from "components/Breadcrumbs";
import Button from "components/Button";
import Link from "components/Link";
import SEO from "components/SEO";
import Text from "components/Text";
import SessionContext from "components/session/SessionContext";
import { pagePadding } from "data/commonClasses";
import {
  dateFormatter,
  fetchFromAPI,
  fetchPost,
  getCookie,
  getTranslationContent,
} from "functions";
import Image from "next/image";
import { useContext, useEffect, useState } from "react";
import useStyles from "styles/Training.Style";
import { twMerge } from "tailwind-merge";
import useTranslation from "translations/hooks/useTranslation";

export default function Training({ data }) {
  const classes = useStyles();
  const { t } = useTranslation();

  let {
    session: {
      user: { _id, authToken },
      lang,
    },
  } = useContext(SessionContext);
  let isAr = lang === "ar";

  const [isRegistered, setIsRegistered] = useState(false);
  const [likeCount, setLikeCount] = useState(data?.interactionsSummary?.likes);
  const [likeByMe, setLikeByMe] = useState(
    data?.interactionsSummary?.likedByMe
  );

  useEffect(() => {
    async function handleView() {
      let header = { _id, authToken };
      let reqBody = { _id: data?._id, collection: "trainings" };
      await fetchFromAPI({ api: "interactionSummary/view", reqBody, header });
    }
    handleView();

    function fetchData() {
      let header = { _id, authToken };
      let reqBody = { _id: data?._id };
      fetchFromAPI({
        api: "registrations/getRegistration",
        reqBody,
        header,
      }).then((response) => {
        let registration = response?.response?._id;
        if (registration) setIsRegistered(true);
      });
    }

    if (_id && authToken && data?._id) fetchData();
  }, [_id, authToken, data?._id]);

  async function handleLike() {
    setLikeCount(likeCount + 1);
    let header = { _id, authToken };
    if (authToken) {
      if (likeByMe) {
        setLikeCount(likeCount - 1);
        setLikeByMe(false);
      } else {
        setLikeCount(likeCount + 1);
        setLikeByMe(true);
      }
    } else setLikeCount(likeCount + 1);
    let reqBody = { _id: data?._id, collection: "trainings" };
    let response = {};

    response = await fetchFromAPI({
      api: "interactionSummary/like",
      reqBody,
      header,
    });

    if (response.status == "error") {
      if (authToken) {
        if (likeByMe) {
          setLikeCount(likeCount - 1);
          setLikeByMe(false);
        } else {
          setLikeCount(likeCount + 1);
          setLikeByMe(true);
        }
      } else setLikeCount(likeCount - 1);
    }
  }

  async function handleShare() {
    navigator.clipboard.writeText(window?.location?.href);
    let header = { _id, authToken };
    let reqBody = { _id: data._id, collection: "trainings" };
    let response = {};

    response = await fetchFromAPI({
      api: "interactionSummary/share",
      reqBody,
      header,
    });
  }

  var breadcrumbs = [
    { text: t("home"), href: "/" },
    { text: t("Training-Center"), href: `/trainings/1` },
    { text: `${data?.title || ""}` },
  ];

  return (
    <div
      className={twMerge(
        "flex flex-col w-full pt-10 min-h-screen",
        pagePadding
      )}
    >
      <SEO
        title={getTranslationContent(data, "title", lang)}
        description={getTranslationContent(data, "description", lang)}
      />
      <Breadcrumbs items={breadcrumbs} />
      <div className={"flex flex-col justify-center items-center pt-5"}>
        {data?.image ? (
          <div
            className={
              "bg-lightGrey p-5  w-full flex flex-col md:flex-row items-start rounded-button"
            }
          >
            <Image
              src={`${data?.image}`}
              alt="training image"
              className="rounded-button"
              layout="intrinsic"
              height={600}
              width={500}
            />
            <div className="md:mx-5 w-full pr-5 pt-5">
              <Text
                sectionTitle
                bold
                text={getTranslationContent(data, "title", lang)}
                className={`text-black text-center ${
                  isAr ? "md:text-right" : "md:text-left"
                }`}
              />
              <div
                className={`flex flex-row gap-5 mt-2 items-center justify-center md:justify-start md:items-start`}
              >
                <div
                  onClick={handleShare}
                  className={"cursor-pointer flex flex-row items-center"}
                >
                  <div className="text-input md:text-infoText mr-2 text-transparentBlack">
                    {t("shares")}
                  </div>
                  <Icon
                    icon={"material-symbols:share"}
                    className="mr-2 w-5 h-5 text-transparentBlack"
                  />
                </div>
                <div
                  onClick={handleLike}
                  className={"cursor-pointer flex flex-row items-center"}
                >
                  <div className="text-input md:text-infoText mr-2 text-transparentBlack">
                    {data?.interactionsSummary?.likes} {t("likes")}
                  </div>
                  <Icon
                    icon={"solar:like-bold"}
                    className="mr-2 w-5 h-5 text-transparentBlack"
                  />
                </div>
                <div className={"flex flex-row items-center"}>
                  <div className="text-input md:text-infoText mr-2 text-transparentBlack">
                    {data?.interactionsSummary?.views} {t("views")}
                  </div>
                  <Icon
                    icon={"carbon:view-filled"}
                    className="mr-2 w-5 h-5 text-transparentBlack"
                  />
                </div>
              </div>

              <div className="w-full flex flex-col justify-center mt-8">
                <div className="flex justify-center mx-5">
                  <ul className="list-disc w-full">
                    <li className=" text-lg sm:text-base">
                      <span className=" font-bold text-black">{t("dateStart")} :</span>
                      <span
                        className={`mx-2 text-black ${isAr ? "text-right" : "text-left"}`}
                      >
                        {dateFormatter(data?.start, "DDDD D MMMM YYYY", isAr)}
                      </span>
                    </li>

                    <li className="text-lg sm:text-base mt-2">
                      <span className=" font-bold text-black">{t("dateEnd")} :</span>
                      <span
                        className={`mx-2 text-black ${isAr ? "text-right" : "text-left"}`}
                      >
                        {dateFormatter(data?.end, "DDDD D MMMM YYYY", isAr)}
                      </span>
                    </li>

                    <li className="text-lg sm:text-base mt-2">
                      <span className="text-black font-bold">{t("instructors")} :</span>
                      <span>
                        <span
                          className={`text-black w-3/4 ${
                            isAr ? "text-right" : "text-left"
                          }`}
                        >
                          {data?.instructors.map((instructor, index) => (
                            <span key={instructor.name}>
                              {instructor.name}
                              {index < data.instructors.length - 1 && " - "}
                            </span>
                          ))}
                        </span>
                      </span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="w-full flex justify-center items-center md:justify-start">
                {data?.closedRegistration ? (
                  <Text
                    text={t("registerClosed")}
                    red={true}
                    className={"mx-2 mt-5"}
                  ></Text>
                ) : isRegistered ? (
                  <>
                    <div className={classes.alreadyRegistered}>
                      {t("alreadyRegistered")}
                    </div>
                    <Link
                      href="/myRegistrations/[to]/details/[slug]"
                      as={`/myRegistrations/training/details/${data.slug}`}
                    >
                      <div className={classes.registerButtonContainer}>
                        <div className={classes.registerButton}>
                          {t("viewRegistrationDetails")}
                        </div>
                      </div>
                    </Link>
                  </>
                ) : (
                  <Link
                    href="/register/[to]/[slug]"
                    as={`/register/training/${data?.slug}`}
                  >
                    <Button title={t("register")} className="mt-10" />
                  </Link>
                )}
              </div>
            </div>
          </div>
        ) : null}

        <div className={"bg-lightGrey rounded-button p-5 mt-5 mb-10"}>
          <div className={"text-black py-5"}>
            {getTranslationContent(data, "description", lang)}
          </div>
        </div>
      </div>
    </div>
  );
}

export async function getServerSideProps(context) {
  let { slug } = context.query;
  let header = {
    _id: getCookie("_id", context.req),
    authToken: getCookie("authToken", context.req),
  };
  let data = {};
  let response = {};
  context.req
    ? (response = await fetchPost({
        apiName: "trainings",
        reqBody: { slug },
        header,
      }))
    : (response = await fetchFromAPI({
        api: "trainings",
        reqBody: { slug },
        header,
      }));
  data = response.response;
  return { props: { data } };
}
